/* KI: six tasks as tabs on the left, one card on the right – what it gets, what it leaves behind, who decides.
   Plays through the tasks while in view; a click takes over for a while (same rhythm as the demonstration). */
import { useCallback, useEffect, useRef, useState } from "react";
import { AiDemo } from "./AiDemos";

const STEP_MS = 7000;
const MANUAL_MS = 30000;

const SparkleIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.5 5.5l2.8 2.8M15.7 15.7l2.8 2.8M18.5 5.5l-2.8 2.8M8.3 15.7l-2.8 2.8" />
  </svg>
);

const CheckIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export const AiExplorer = ({ ai, inView }) => {
  const n = ai.tasks.length;
  const [st, setSt] = useState({ v: 0, loop: 0 });
  const [hover, setHover] = useState(false);
  const manualUntil = useRef(0);
  const playing = inView && !hover;

  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => setSt((s) => (manualUntil.current > Date.now() ? { v: s.v, loop: s.loop + 1 } : { v: (s.v + 1) % n, loop: s.loop + 1 })), STEP_MS);
    return () => clearTimeout(t);
  }, [st, playing, n]);

  const select = useCallback((i) => {
    manualUntil.current = Date.now() + MANUAL_MS;
    setSt((s) => ({ v: i, loop: s.loop + 1 }));
  }, []);

  const onKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const dir = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
    const next = (st.v + dir + n) % n;
    select(next);
    e.currentTarget.querySelectorAll("[role=tab]")[next]?.focus();
  };

  const cur = ai.tasks[st.v];
  const manual = manualUntil.current > Date.now();
  const progress = ((st.v + 1) / n) * 100;
  return (
    <div className="kvl-wide aix" data-testid="tilbod-ai-explorer" data-task={cur.key} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <ol
        className="aix-tabs"
        role="tablist"
        aria-label={ai.hint}
        onKeyDown={onKey}
        data-testid="tilbod-ai-tabs"
        style={{ "--progress": `${progress}%` }}
      >
        {ai.tasks.map((x, i) => {
          const on = i === st.v;
          const done = i < st.v;
          return (
            <li key={x.key} className={`aix-tab-li ${on ? "is-on" : ""} ${done ? "is-done" : ""}`} style={{ "--i": i }}>
              <button
                type="button"
                role="tab"
                id={`aix-tab-${x.key}`}
                aria-selected={on}
                aria-controls="aix-panel"
                tabIndex={on ? 0 : -1}
                className={`aix-tab ${on ? "is-on" : ""} ${done ? "is-done" : ""}`}
                onClick={() => select(i)}
                data-testid={`tilbod-ai-tab-${i + 1}`}
              >
                <span className="aix-tab-dot" aria-hidden="true">
                  {done ? <CheckIcon className="aix-tab-check" /> : <span className="aix-tab-n">{String(i + 1).padStart(2, "0")}</span>}
                </span>
                <span className="aix-tab-txt">
                  <span className="aix-tab-verb">{x.verb}</span>
                  <span className="aix-tab-what">{x.what}</span>
                </span>
                {on && <span key={st.loop} className={`aix-tab-bar ${playing && !manual ? "is-run" : ""}`} aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="aix-panel" id="aix-panel" role="tabpanel" aria-labelledby={`aix-tab-${cur.key}`} data-testid="tilbod-ai-panel">
        <div key={cur.key} className="aix-panel-in">
          <header className="aix-panel-head">
            <span className="aix-chip" aria-hidden="true">
              <SparkleIcon className="aix-chip-icon" />
              <span className="aix-chip-txt">KI-arbeid</span>
              <span className="aix-chip-step">{String(st.v + 1).padStart(2, "0")}<span className="aix-chip-slash">/</span>{String(n).padStart(2, "0")}</span>
            </span>
            <h3 className="aix-panel-title" data-testid="tilbod-ai-panel-title">
              {cur.verb}
            </h3>
            <p className="aix-panel-what">{cur.what}</p>
          </header>
          <div className="aix-demo" data-kind={cur.demo.kind} data-testid="tilbod-ai-demo">
            <AiDemo demo={cur.demo} />
          </div>
          <dl className="aix-io" data-testid="tilbod-ai-io">
            {[
              ["in", ai.cols.in, cur.in],
              ["out", ai.cols.out, cur.out],
              ["who", ai.cols.who, cur.who],
            ].map(([k, label, v]) => (
              <div key={k} className={`aix-io-${k}`}>
                <dt>{label}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};
